import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { safeFormatDate } from '../../../shared';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Tab,
  Tabs,
  Button,
  CircularProgress,
} from '@mui/material';
import { CheckCircleOutline as ResolveIcon, DeleteOutline as DeleteIcon, Search as InvestigateIcon } from '@mui/icons-material';
import DetailSidebar from '../../../shared/components/DetailSidebar';
import DetailRow from '../../../shared/components/DetailRow';
import MarkdownContent from '../../../shared/components/MarkdownContent';
import InlineAlert from '../../../shared/components/InlineAlert';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { useEventManagementService } from '../services/eventManagementService';
import { AutomationContentType } from '../../../types/backend';
import type { ActionLogEntry } from '../../../types/backend';
import {
  opsItemKind, severityMeta, statusLabel, KIND_META, KindIcon,
} from '../utils/opsitemDisplay';

interface ActionLogDetailPanelProps {
  entry: ActionLogEntry;
  customerId: string;
  onClose: () => void;
  isAdmin: boolean;
  onCloseItem: (opsitemId: string) => Promise<void>;
  onDeleteItem: (opsitemId: string) => Promise<void>;
  onStartInvestigation: (entry: ActionLogEntry) => void;
}

const ActionLogDetailPanel: React.FC<ActionLogDetailPanelProps> = ({
  entry,
  customerId,
  onClose,
  isAdmin,
  onCloseItem,
  onDeleteItem,
  onStartInvestigation,
}) => {
  const kind = opsItemKind(entry);
  const isReport = kind === 'report';
  const [tab, setTab] = useState<'details' | 'report'>('details');

  useEffect(() => {
    setTab('details');
  }, [entry.opsitem_id]);

  const toolbar = isReport ? (
    <>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: 2, minHeight: 36 }}>
        <Tab label="Details" value="details" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
        <Tab label="Report" value="report" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
      </Tabs>
      <Divider />
    </>
  ) : undefined;

  return (
    <DetailSidebar title={entry.title} onClose={onClose} toolbar={toolbar}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {tab === 'report' ? (
            <ReportView customerId={customerId} opsitemId={entry.opsitem_id} />
          ) : (
            <DetailsView entry={entry} />
          )}
        </Box>
        <ActionLogActions
          entry={entry}
          isAdmin={isAdmin}
          onCloseItem={onCloseItem}
          onDeleteItem={onDeleteItem}
          onPanelClose={onClose}
          onStartInvestigation={onStartInvestigation}
        />
      </Box>
    </DetailSidebar>
  );
};

const ActionLogActions: React.FC<{
  entry: ActionLogEntry;
  isAdmin: boolean;
  onCloseItem: (opsitemId: string) => Promise<void>;
  onDeleteItem: (opsitemId: string) => Promise<void>;
  onPanelClose: () => void;
  onStartInvestigation: (entry: ActionLogEntry) => void;
}> = ({ entry, isAdmin, onCloseItem, onDeleteItem, onPanelClose, onStartInvestigation }) => {
  const [busy, setBusy] = useState<'close' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const alreadyResolved = entry.status === 'Resolved';

  const handleClose = async () => {
    setBusy('close');
    setError(null);
    try {
      await onCloseItem(entry.opsitem_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve item');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    setBusy('delete');
    setError(null);
    try {
      await onDeleteItem(entry.opsitem_id);
      setConfirmDelete(false);
      onPanelClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
      setConfirmDelete(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', p: 1.5 }}>
      {error && (
        <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </InlineAlert>
      )}
      {/* A narrow pane wraps the buttons onto a second line rather than squeeze their labels past their edges. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, '& > .MuiButton-root': { flexShrink: 0, whiteSpace: 'nowrap' } }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<InvestigateIcon fontSize="small" />}
          onClick={() => onStartInvestigation(entry)}
          disabled={busy !== null}
        >
          Investigate
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={busy === 'close' ? <CircularProgress size={14} /> : <ResolveIcon fontSize="small" />}
          onClick={handleClose}
          disabled={alreadyResolved || busy !== null}
        >
          {alreadyResolved ? 'Resolved' : 'Resolve'}
        </Button>
        {isAdmin && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={() => setConfirmDelete(true)}
            disabled={busy !== null}
          >
            Delete
          </Button>
        )}
      </Box>

      <DeleteConfirmDialog
        open={confirmDelete}
        title="Delete action log item"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        confirmText={busy === 'delete' ? 'Deleting…' : 'Delete'}
      >
        <Typography variant="body2">
          Permanently delete <strong>{entry.title}</strong>? This removes the action log item from
          the operations account and cannot be undone.
        </Typography>
      </DeleteConfirmDialog>
    </Box>
  );
};

const DetailsView: React.FC<{ entry: ActionLogEntry }> = ({ entry }) => {
  const kind = opsItemKind(entry);
  const contentType = entry.automation_result?.content_type || AutomationContentType.TEXT;
  const resultSummary = entry.automation_result?.result_summary || '';

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <KindIcon kind={kind} size={18} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{KIND_META[kind].label}</Typography>
        </Box>

        <DetailRow label="Severity" value={severityMeta(entry.severity).label} />
        <DetailRow label="Status" value={statusLabel(entry.status)} />
        <DetailRow label="Rule" value={entry.rule_name} />
        <DetailRow label="Source" value={entry.source} />
        <DetailRow label="Detail type" value={entry.detail_type} />
        <DetailRow label="Created" value={safeFormatDate(entry.created_at)} />
        {entry.updated_at && (
          <DetailRow label="Updated" value={safeFormatDate(entry.updated_at)} />
        )}
        <DetailRow label="Action log item" value={entry.opsitem_id} />
      </Box>

      {entry.automation_result && (
        <>
          <Divider />
          <Typography variant="subtitle2" fontWeight={600}>
            Automation Result
          </Typography>

          <DetailRow label="Run ID" value={entry.automation_result.run_id} />
          <DetailRow label="Status" value={entry.automation_result.status} />
          <DetailRow
            label="Completed"
            value={entry.automation_result.completed_at ? safeFormatDate(entry.automation_result.completed_at) : undefined}
          />

          {resultSummary && (
            <Box sx={{ mt: 1 }}>
              <AutomationContent content={resultSummary} contentType={contentType} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const ReportView: React.FC<{ customerId: string; opsitemId: string }> = ({ customerId, opsitemId }) => {
  const eventService = useEventManagementService();
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    eventService.getReport(customerId, opsitemId)
      .then((res) => { if (!cancelled) setHtml(res.html); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load report'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventService, customerId, opsitemId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return <InlineAlert severity="error" sx={{ m: 2 }}>{error}</InlineAlert>;
  }
  return (
    <Box
      sx={{
        p: 2,
        '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
        '& th, & td': { border: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
        '& th': { bgcolor: 'action.hover', fontWeight: 'bold' },
        '& img': { maxWidth: '100%' },
      }}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html ?? '') }}
    />
  );
};

const AutomationContent: React.FC<{ content: string; contentType: string }> = ({ content, contentType }) => {
  switch (contentType) {
    case AutomationContentType.MARKDOWN:
      return <MarkdownContent content={content} variant="document" syntaxHighlight />;
    case AutomationContentType.HTML:
      return (
        <Box
          sx={{
            '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
            '& th, & td': { border: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
            '& th': { bgcolor: 'action.hover', fontWeight: 'bold' },
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
        />
      );
    default:
      return (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
          <Typography
            component="pre"
            sx={{ fontFamily: 'monospace', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
          >
            {content}
          </Typography>
        </Paper>
      );
  }
};

export default ActionLogDetailPanel;
